import React, { useState } from 'react';
import { Album } from '../../types/album';
import { theme } from '../../styles/theme';
import { generateSortKey } from '../../utils/sorting';
import { albumUpdateService } from '../../services/albumUpdateService';

interface AlbumEditFormProps {
  album: Album;
  onSave: (album: Album) => void;
  onCancel: () => void;
}

const AlbumEditForm: React.FC<AlbumEditFormProps> = ({ album, onSave, onCancel }) => {
  const [editedAlbum, setEditedAlbum] = useState<Album>({ ...album });
  const [newGenre, setNewGenre] = useState('');
  const [newArtist, setNewArtist] = useState('');

  const handleSave = () => {
    // Ensure primary artist matches first artist in array
    const finalAlbum = {
      ...editedAlbum,
      artist: editedAlbum.artists?.[0] || editedAlbum.artist,
      artists: editedAlbum.artists || [editedAlbum.artist]
    };
    
    // Regenerate sort key if custom sort name or basic info changed
    const updatedAlbum = {
      ...finalAlbum,
      sortKey: generateSortKey(finalAlbum)
    };

    // Determine which fields were manually edited
    const editedFields = [];
    if (updatedAlbum.title !== album.title) editedFields.push('title');
    if (JSON.stringify(updatedAlbum.artists) !== JSON.stringify(album.artists)) editedFields.push('artists');
    if (updatedAlbum.year !== album.year) editedFields.push('year');
    if (JSON.stringify(updatedAlbum.genres) !== JSON.stringify(album.genres)) editedFields.push('genres');
    if (updatedAlbum.customSortName !== album.customSortName) editedFields.push('customSortName');
    if (updatedAlbum.isVariousArtists !== album.isVariousArtists) editedFields.push('isVariousArtists');

    // Mark these fields as manually customized to prevent automatic sync overwrite
    const albumWithCustomFields = albumUpdateService.markFieldsAsCustomized(updatedAlbum, editedFields);
    
    onSave(albumWithCustomFields);
  };

  const addGenre = () => {
    if (newGenre.trim() && !editedAlbum.genres.includes(newGenre.trim())) {
      setEditedAlbum({
        ...editedAlbum,
        genres: [...editedAlbum.genres, newGenre.trim()]
      });
      setNewGenre('');
    }
  };

  const removeGenre = (genreToRemove: string) => {
    setEditedAlbum({
      ...editedAlbum,
      genres: editedAlbum.genres.filter(genre => genre !== genreToRemove)
    });
  };

  const addArtist = () => {
    if (newArtist.trim() && !(editedAlbum.artists || [editedAlbum.artist]).includes(newArtist.trim())) {
      const currentArtists = editedAlbum.artists || [editedAlbum.artist];
      setEditedAlbum({
        ...editedAlbum,
        artists: [...currentArtists, newArtist.trim()]
      });
      setNewArtist('');
    }
  };

  const removeArtist = (artistToRemove: string) => {
    const currentArtists = editedAlbum.artists || [editedAlbum.artist];
    if (currentArtists.length > 1) { // Prevent removing the last artist
      setEditedAlbum({
        ...editedAlbum,
        artists: currentArtists.filter(artist => artist !== artistToRemove)
      });
    }
  };


  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.lg,
  };

  const sectionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '1rem',
    fontWeight: '600',
    color: theme.colors.text,
  };

  const inputStyle: React.CSSProperties = {
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: '0.875rem',
  };

  const tagContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.sm,
  };

  const editableTagStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primaryLight,
    color: 'white',
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  };

  const removeTagButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: 0,
    lineHeight: 1,
  };

  const addInputContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.sm,
  };

  const addButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    color: 'white',
    border: 'none',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '0.875rem',
    cursor: 'pointer',
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: theme.spacing.md,
    justifyContent: 'flex-end',
    marginTop: theme.spacing.xl,
  };

  const saveButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.success,
    color: 'white',
    border: 'none',
    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  };

  const cancelButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.border,
    color: theme.colors.text,
    border: 'none',
    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '1rem',
    cursor: 'pointer',
  };

  return (
    <div style={formStyle}>
      <div style={sectionStyle}>
        <label style={labelStyle}>Album Title</label>
        <input
          type="text"
          value={editedAlbum.title}
          onChange={(e) => setEditedAlbum({ ...editedAlbum, title: e.target.value })}
          style={inputStyle}
        />
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Artists</label>
        <div style={tagContainerStyle}>
          {(editedAlbum.artists || [editedAlbum.artist]).map((artist, index) => (
            <div key={index} style={editableTagStyle}>
              {artist}
              {(editedAlbum.artists || [editedAlbum.artist]).length > 1 && (
                <button
                  type="button"
                  onClick={() => removeArtist(artist)}
                  style={removeTagButtonStyle}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: theme.spacing.sm, marginTop: theme.spacing.sm }}>
          <input
            type="text"
            value={newArtist}
            onChange={(e) => setNewArtist(e.target.value)}
            placeholder="Add artist"
            style={inputStyle}
            onKeyDown={(e) => e.key === 'Enter' && addArtist()}
          />
          <button type="button" onClick={addArtist} style={addButtonStyle}>
            Add
          </button>
        </div>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Year</label>
        <input
          type="number"
          value={editedAlbum.year || ''}
          onChange={(e) => setEditedAlbum({ ...editedAlbum, year: parseInt(e.target.value) || 0 })}
          style={inputStyle}
          min="1900"
          max="2030"
        />
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Custom Sort Name (Optional)</label>
        <input
          type="text"
          value={editedAlbum.customSortName || ''}
          onChange={(e) => setEditedAlbum({ ...editedAlbum, customSortName: e.target.value || undefined })}
          style={inputStyle}
          placeholder="Override automatic sorting (e.g., 'Beatles' for 'The Beatles')"
        />
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Genres</label>
        <div style={tagContainerStyle}>
          {editedAlbum.genres.map((genre, index) => (
            <div key={index} style={editableTagStyle}>
              {genre}
              <button
                style={removeTagButtonStyle}
                onClick={() => removeGenre(genre)}
                title="Remove genre"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div style={addInputContainerStyle}>
          <input
            type="text"
            value={newGenre}
            onChange={(e) => setNewGenre(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addGenre()}
            style={{ ...inputStyle, flex: 1 }}
            placeholder="Add new genre..."
          />
          <button style={addButtonStyle} onClick={addGenre}>
            Add
          </button>
        </div>
      </div>


      <div style={sectionStyle}>
        <label style={labelStyle}>
          <input
            type="checkbox"
            checked={editedAlbum.isVariousArtists}
            onChange={(e) => setEditedAlbum({ ...editedAlbum, isVariousArtists: e.target.checked })}
            style={{ marginRight: theme.spacing.sm }}
          />
          Various Artists Compilation
        </label>
      </div>

      {editedAlbum.format && (
        <div style={sectionStyle}>
          <label style={labelStyle}>Format Information</label>
          <div style={{
            padding: theme.spacing.md,
            backgroundColor: theme.colors.background,
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.border}`,
            fontSize: '0.875rem'
          }}>
            <div style={{ marginBottom: theme.spacing.xs, color: theme.colors.text }}>
              <strong>Type:</strong> {editedAlbum.format.type}
            </div>
            <div style={{ marginBottom: theme.spacing.xs, color: theme.colors.text }}>
              <strong>Packaging:</strong> {editedAlbum.format.packaging}
            </div>
            <div style={{ marginBottom: theme.spacing.xs, color: theme.colors.text }}>
              <strong>Disc Count:</strong> {editedAlbum.format.discCount}
            </div>
            <div style={{ marginBottom: theme.spacing.xs, color: theme.colors.text }}>
              <strong>Physical Size:</strong> {editedAlbum.format.physicalSize.toFixed(1)}× standard thickness
            </div>
            {editedAlbum.format.formatDetails.length > 0 && (
              <div style={{ color: theme.colors.textSecondary }}>
                <strong>Details:</strong> {editedAlbum.format.formatDetails.join(', ')}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={buttonContainerStyle}>
        <button style={cancelButtonStyle} onClick={onCancel}>
          Cancel
        </button>
        <button style={saveButtonStyle} onClick={handleSave}>
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default AlbumEditForm;